import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  addBookingAction,
  getOneEventAction,
} from "../store/eventManagement/actions";
import moment from "moment";
import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import OrderSummary from "../components/card/OrderSummary";
import { Checkbox, notification, Spin } from "antd";
import { isValidEmail } from "../utils/helper";
import { DefaultPlaceholder } from "../components/skeleton/Skeleton";

const format = "ddd, Do MMM YYYY";

const BookingPage = () => {
  const { eventManagement } = useSelector((state) => state);
  const { eventId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [cart, setCart] = useState({});
  const [receiverInfo, setReceiverInfo] = useState({});
  const [singleEmail, setSingleEmail] = useState({});
  const [useSingleEmail, setUseSingleEmail] = useState({});

  const cartItems = Object.values(cart);

  const handleBooking = async () => {
    for (const ticketId in cart) {
      const ticket = cart[ticketId];
      if (useSingleEmail[ticketId]) {
        const { name, email } = singleEmail[ticketId] || {};
        if (!name || !email || !isValidEmail(email)) {
          notification.error({
            message: `Please provide a valid name or email for the ticket type ${ticket.ticket?.type}`,
          });

          return;
        }
      } else {
        for (let i = 0; i < ticket.quantity; i++) {
          const receiver = receiverInfo[ticketId]?.[i] || {};
          if (
            !receiver.name ||
            !receiver.email ||
            !isValidEmail(receiver.email)
          ) {
            notification.error({
              message: `Please provide valid name or email for receiver ${
                i + 1
              } of ticket type ${ticket.ticket?.type}`,
            });
            return;
          }
        }
      }
    }

    const payload = {
      eventId: eventId,
      tickets: cartItems.map((d) => ({
        ticket: d.ticket._id,
        quantity: d.quantity,
        ticketHolder: useSingleEmail[d.ticket._id]
          ? Array(d.quantity).fill({
              name: singleEmail[d.ticket._id].name,
              email: singleEmail[d.ticket._id].email,
            })
          : receiverInfo[d.ticket._id] || [],
      })),
    };
    const res = await addBookingAction(payload)(dispatch);
    if (res.type) {
      navigate(`/event-details/${eventId}/checkout/${res?.data?._id}`);
    }
  };

  const addTicketToCart = (ticketId) => {
    setCart((prevCart) => {
      const currentQty = prevCart[ticketId]?.quantity || 0;
      return {
        ...prevCart,
        [ticketId]: {
          quantity: currentQty + 1,
          ticket: event.tickets.find((ticket) => ticket._id === ticketId),
        },
      };
    });
  };

  const removeTicketFromCart = (ticketId) => {
    setCart((prevCart) => {
      const currentQty = prevCart[ticketId]?.quantity || 0;
      if (currentQty <= 1) {
        const newCart = { ...prevCart };
        delete newCart[ticketId];
        setReceiverInfo((prev) => {
          const updatedInfo = { ...prev };
          delete updatedInfo[ticketId];
          return updatedInfo;
        });
        return newCart;
      }
      return {
        ...prevCart,
        [ticketId]: {
          quantity: currentQty - 1,
          ticket: event.tickets.find((ticket) => ticket._id === ticketId),
        },
      };
    });
  };

  const handleReceiverChange = (ticketId, index, field, value) => {
    setReceiverInfo((prev) => {
      const ticketReceivers = prev[ticketId] || [];
      const updatedReceivers = [...ticketReceivers];
      updatedReceivers[index] = {
        ...(updatedReceivers[index] || {}),
        [field]: value,
      };
      return {
        ...prev,
        [ticketId]: updatedReceivers,
      };
    });
  };

  const handleSingleEmailChange = (ticketId, field, value) => {
    setSingleEmail((prev) => ({
      ...prev,
      [ticketId]: {
        ...prev[ticketId],
        [field]: value,
      },
    }));
  };

  const toggleUseSingleEmail = (ticketId, checked) => {
    setUseSingleEmail((prev) => ({
      ...prev,
      [ticketId]: checked,
    }));
  };

  const event = eventManagement?.selected;

  useEffect(() => {
    if (eventId) {
      getOneEventAction(eventId)(dispatch);
    }
  }, [dispatch, eventId]);

  return (
    <div className="min-h-[calc(100vh-200px)] pt-10">
      <div className="container mx-auto p-4">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="lg:w-2/3">
            <div className="shadow mb-6 dark:bg-[#24262a] rounded-md">
              <div className="p-4 -mb-2.5">
                <h2 className="text-xl font-bold -mb-0.5">{event?.title}</h2>
                <span>
                  {event && event.date ? moment(event.date).format(format) : ""}
                </span>
              </div>
              <div className="p-4">
                <h4 className="mb-6">Available tickets</h4>
                <div className="flex flex-col space-y-4">
                  {event?.tickets
                    ?.filter((d) => d?.quantity !== 0)
                    ?.map((el) => (
                      <div
                        key={el._id}
                        className="border-2 rounded-md border-default p-4"
                      >
                        <div className="flex items-center justify-between mb-2 mr-4">
                          <h4>{el?.type}</h4>
                          <div className="flex items-center gap-x-2 mb-1">
                            <MinusCircleOutlined
                              className="text-xl cursor-pointer"
                              onClick={() => removeTicketFromCart(el._id)}
                            />
                            <span className="text-2xl dark:text-white">
                              {cart[el._id]?.quantity || 0}
                            </span>
                            <PlusCircleOutlined
                              className="text-xl cursor-pointer"
                              onClick={() => addTicketToCart(el._id)}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className="flex flex-col">
                          <div className="flex space-x-4 items-center mt-2">
                            <h4 className="">Price</h4>
                            <p>{el?.price}</p>
                          </div>

                          <p className="mt-1 mb-2.5">
                            Available Tickets <span>{el?.quantity}</span>
                          </p>
                        </div>

                        {cartItems?.length > 0 && (
                          <Checkbox
                            checked={useSingleEmail[el._id] || false}
                            onChange={(e) =>
                              toggleUseSingleEmail(el._id, e.target.checked)
                            }
                          >
                            Use the same email for all tickets of this type
                          </Checkbox>
                        )}

                        {useSingleEmail[el._id] ? (
                          <div className="mt-2">
                            <div className="flex space-x-4">
                              <input
                                type="text"
                                placeholder="Receiver Name"
                                value={singleEmail[el._id]?.name || ""}
                                onChange={(e) =>
                                  handleSingleEmailChange(
                                    el._id,
                                    "name",
                                    e.target.value
                                  )
                                }
                                className="border p-2 rounded w-full outline-none"
                              />
                              <input
                                type="email"
                                placeholder="Receiver Email"
                                value={singleEmail[el._id]?.email || ""}
                                onChange={(e) =>
                                  handleSingleEmailChange(
                                    el._id,
                                    "email",
                                    e.target.value
                                  )
                                }
                                className="border p-2 rounded w-full outline-none"
                              />
                            </div>
                          </div>
                        ) : (
                          Array.from({
                            length: cart[el._id]?.quantity || 0,
                          }).map((_, index) => (
                            <div
                              key={index}
                              className="mt-2 grid grid-cols-2 gap-2"
                            >
                              <input
                                type="text"
                                placeholder={`Receiver Name ${index + 1}`}
                                value={
                                  receiverInfo[el._id]?.[index]?.name || ""
                                }
                                onChange={(e) =>
                                  handleReceiverChange(
                                    el._id,
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                                className="border p-2 rounded w-full outline-none"
                              />
                              <input
                                type="email"
                                placeholder={`Receiver Email ${index + 1}`}
                                value={
                                  receiverInfo[el._id]?.[index]?.email || ""
                                }
                                onChange={(e) =>
                                  handleReceiverChange(
                                    el._id,
                                    index,
                                    "email",
                                    e.target.value
                                  )
                                }
                                className="border p-2 rounded w-full outline-none"
                              />
                            </div>
                          ))
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:w-1/3">
            <div className="shadow p-4 dark:bg-[#24262a] rounded-md">
              {event?.imageUrl ? (
                <img
                  src={event?.imageUrl}
                  alt={event._id}
                  className="w-full h-[200px] object-cover rounded-2xl opacity-90 shadow-md"
                />
              ) : (
                <DefaultPlaceholder />
              )}

              <div className="min-h-20 dark:bg-primary-darkBg mt-4 rounded-md">
                <OrderSummary cart={cart} />
              </div>

              <button
                className={`${
                  cartItems.length === 0 || eventManagement?.isCreatingBooking
                    ? "cursor-not-allowed"
                    : ""
                } w-full bg-primary text-white py-2.5 px-4 rounded transition duration-300 mt-6 text-center`}
                onClick={handleBooking}
                disabled={
                  cartItems.length === 0 || eventManagement?.isCreatingBooking
                }
              >
                {eventManagement?.isCreatingBooking ? <Spin /> : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
